var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ApplicationPanelWithTable } from '../ApplicationPanelWithTable';
import { PanelNames } from '../../UtilsClasses/FactoryConstants';
import OptionChainPanelTemplate from '@front/templates/OptionChainPanelTemplate.html';
import { QuickTableZone } from '../../elements/QuickTable/QuickTable';
import { OptionChainTableItem } from '../../cache/OptionMaster/OptionChainTableItem';
import { OptionChainStrikesListTableItem } from '../../cache/OptionMaster/OptionChainStrikesListTableItem';
import { Resources } from '@shared/localizations/Resources';
import { ColouringModes } from '../../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../../misc/ThemeManager';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { DynProperty, DynPropertyRelationType } from '@shared/commons/DynProperty';
import { getStrikesAggregationStyleKey, StrikesAggregationStyle } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionChain/StrikesAggregationStyle';
import { getStrikesColoringMethodKey, StrikesColoringMethod } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionChain/StrikesColoringMethod';
import { getHistoricalVolatilityPriceTypeKey, HistoricalVolatilityPriceType } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionChain/HistoricalVolatilityPriceType';
import { OptionTraderUtils } from '@shared/commons/cache/OptionMaster/OptionTrader/OptionTraderUtils';
import { MainWindowManager } from '../../UtilsClasses/MainWindowManager';
import { CustomerAccess } from '@shared/commons/CustomerAccess/CustomerAccess';
import { createContextMenuItemsFromEnum, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { StrikePriceColumnRenderer } from '../../elements/QuickTable/Renderers/StrikePriceColumnRenderer';
export class OptionChainPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this._isInitialized = false;
        this._isSkipColumnsVisibleChangedEvent = false;
        this._isRecolorRows = true;
        this._isRecenterTable = true;
        this.onBeforeTableContextMenuShowing = (sender) => {
            const selectedItem = sender.getSelectedTableItems()[0];
            sender.setTableContextMenuItems([]);
            if (isNullOrUndefined(selectedItem)) {
                return;
            }
            const account = this._optionTrader.account;
            const instrument = selectedItem.instrument;
            const contextMenuItems = [];
            if (CustomerAccess.panelAllowed(PanelNames.AdvancedOrderEntry)) {
                contextMenuItems.push({
                    type: NewContextMenuItemType.Default,
                    text: 'panel.neworder',
                    event: () => {
                        var _a;
                        (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.AdvancedOrderEntry, null, (panel) => {
                            panel.set({
                                account,
                                instrument
                            });
                        });
                    }
                });
            }
            if (CustomerAccess.panelAllowed(PanelNames.ChartPanel)) {
                contextMenuItems.push({
                    type: NewContextMenuItemType.Default,
                    text: 'panel.chart',
                    event: () => {
                        var _a;
                        (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.ChartPanel, null, (panel) => {
                            panel.set({
                                accountItem: account,
                                instrumentItem: instrument
                            });
                        });
                    }
                });
            }
            if (contextMenuItems.length > 0) {
                sender.setTableContextMenuItems(contextMenuItems);
            }
        };
        this.Name = 'OptionChainPanel';
        this.headerLocaleKey = 'panel.optionChain';
        this.NeedCalculateRowCount = false;
    }
    // #region Overrides
    getType() {
        return PanelNames.OptionChainPanel;
    }
    oninit() {
        super.oninit();
        this.onInstrumentChanged = this.onInstrumentChanged.bind(this);
        this.populateStrikes = this.populateStrikes.bind(this);
        this.onQuoteChanged = this.onQuoteChanged.bind(this);
        super.observe('selectedSerieComboboxItem', this.onSelectedSerieComboboxItemChanged);
        super.observe('visible', this.onVisibleChanged);
    }
    onteardown() {
        this.teardownPutCallQuickTable(this._putsQuickTable);
        this.teardownPutCallQuickTable(this._callsQuickTable);
        this._strikesListQuickTable.OnSelectionChanged.UnSubscribe(this.onQuickTableSelectionChanged, this);
        this._strikesListQuickTable.OnHoverChanged.UnSubscribe(this.onQuickTableHoverChanged, this);
        this._strikesListQuickTable.scroll.OnValueChange.UnSubscribe(this.onQuickTableScroll, this);
        this._optionTrader.unsubscribeOnInstrumentChanged(this.onInstrumentChanged);
        this._optionTrader.unsubscribeOnSeriesDateChanged(this.populateStrikes);
        this._optionTrader.unsubscribeOnQuoteChanged(this.onQuoteChanged);
        super.onteardown();
    }
    jbInit() {
        this.initializeVariables();
        super.jbInit();
        this.initPutCallQuickTable(this._putsQuickTable);
        this.initPutCallQuickTable(this._callsQuickTable);
        this._strikesListQuickTable.allowGroupBy = false;
        this._strikesListQuickTable.lockManualSorting = true;
        this._strikesListQuickTable.canShowHeaderMenu = false;
        this._strikesListQuickTable.CustomTextAlign = 'center';
        this._strikesListQuickTable.InitializeDirect(new OptionChainStrikesListTableItem(null));
        this._strikesListQuickTable.columns[OptionChainStrikesListTableItem.STRIKE_COL_INDEX].renderer =
            new StrikePriceColumnRenderer();
        this._strikesListQuickTable.UpdateSortedColumns();
        this._strikesListQuickTable.SetScrollHidden(true);
        this._strikesListQuickTable.OnSelectionChanged.Subscribe(this.onQuickTableSelectionChanged, this);
        this._strikesListQuickTable.OnHoverChanged.Subscribe(this.onQuickTableHoverChanged, this);
        this._strikesListQuickTable.scroll.OnValueChange.Subscribe(this.onQuickTableScroll, this);
    }
    layoutTable() {
        super.layoutTable();
        for (const table of super.findAllComponents('quickTableRactive')) {
            super.layoutTableResize(table);
        }
    }
    localize() {
        super.localize();
        if (!isNullOrUndefined(this._optionTrader))
            this.populateSeriesCombobox();
        this._callsQuickTable.localize();
        this._putsQuickTable.localize();
        this._strikesListQuickTable.localize();
        super.set('callsLabel', Resources.getResource('panel.optionChain.call'));
        super.set('putsLabel', Resources.getResource('panel.optionChain.put'));
    }
    callThemeChange() {
        this.themeChange();
    }
    callCallback(properties) {
        this.callBack(properties);
    }
    themeChange() {
        super.themeChange();
        if (!this._isInitialized) {
            return;
        }
        if (isNullOrUndefined(this._optionTrader)) {
            return;
        }
        const optionChain = this._optionTrader.optionChain;
        optionChain.inTheMoneyColor = ThemeManager.colors.OptionMasterInTheMoneyColor;
        optionChain.outTheMoneyColor = ThemeManager.colors.OptionMasterOutTheMoneyColor;
        optionChain.atmStrikeColor = ThemeManager.colors.OptionMasterATMStrikeColor;
        optionChain.standardDeviationColor = ThemeManager.colors.OptionMasterStandardDeviationColor;
        optionChain.secondStandardDeviationColor = ThemeManager.colors.OptionMasterSecondStandardDeviationColor;
        optionChain.askColor = ThemeManager.colors.OptionMasterAskColor;
        optionChain.bidColor = ThemeManager.colors.OptionMasterBidColor;
        super.ProcessSetColumnsColouringMode(this._callsQuickTable, OptionChainTableItem.prevValueColumnsIndexes, ColouringModes.Previous);
        super.ProcessSetColumnsColouringMode(this._putsQuickTable, OptionChainTableItem.prevValueColumnsIndexes, ColouringModes.Previous);
        const tables = super.findAllComponents('quickTableRactive');
        for (const table of tables) {
            table.themeChange();
        }
        const tableBackgroundSelected = ThemeManager.colors.OptionMasterTableRowBackgroundSelected;
        const tableForegroundSelected = ThemeManager.colors.OptionMasterTableRowForegroundSelected;
        this._callsQuickTable.selectedBackColor =
            this._putsQuickTable.selectedBackColor =
                this._strikesListQuickTable.selectedBackColor =
                    tableBackgroundSelected;
        this._callsQuickTable.selectedForeColor =
            this._putsQuickTable.selectedForeColor =
                this._strikesListQuickTable.selectedForeColor =
                    tableForegroundSelected;
        const strikesTableRowBackground = ThemeManager.colors.OptionMasterStrikeRowBackground;
        const strikesTableRowForeground = ThemeManager.colors.OptionMasterStrikeRowForeground;
        this._strikesListQuickTable.BackColor = strikesTableRowBackground;
        this._strikesListQuickTable.ForeColor = strikesTableRowForeground;
        this._strikesListQuickTable.rowBackColor = strikesTableRowBackground;
        this._strikesListQuickTable.secondRowBackColor = strikesTableRowBackground;
        this._isRecolorRows = true;
    }
    TickAsync() {
        super.TickAsync();
        if (!this._isInitialized) {
            return;
        }
        const isVisible = this.get('visible');
        if (!isVisible) {
            return;
        }
        if (this._isRecolorRows) {
            this._isRecolorRows = false;
            this.recolorRows(this._callsQuickTable);
            this.recolorRows(this._putsQuickTable);
        }
        if (this._isRecenterTable) {
            const centerPrice = this.getCenterPrice();
            if (isValidNumber(centerPrice)) {
                this._isRecenterTable = false;
                this.recenterTable(centerPrice);
            }
        }
        this._callsQuickTable.Draw();
        this._callsQuickTable.needRedraw = false;
        this._callsQuickTable.needRedrawBackground = true;
        this._putsQuickTable.Draw();
        this._putsQuickTable.needRedraw = false;
        this._putsQuickTable.needRedrawBackground = true;
        this._strikesListQuickTable.Draw();
        this._strikesListQuickTable.needRedraw = false;
    }
    repopulate() {
        super.repopulate();
        this.populateSeriesCombobox();
    }
    updateSettings() {
        super.updateSettings();
        this.updateTableEditingInfo(this._callsQuickTable);
        this.updateTableEditingInfo(this._putsQuickTable);
    }
    Properties() {
        const properties = super.Properties();
        let index = 0;
        const separatorGroup = '#0#' + Resources.getResource('property.View');
        let dp = new DynProperty('property.StrikesAggregationStyle', this._optionTrader.optionChain.strikesAggregationStyle, DynProperty.COMBOBOX, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(StrikesAggregationStyle, getStrikesAggregationStyleKey);
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        dp.assignedProperty = ['property.CustomUnderlierPrice'];
        dp.DynPropertyRelationType = DynPropertyRelationType.Visibility;
        properties.push(dp);
        dp = new DynProperty('property.CustomUnderlierPrice', this._optionTrader.optionChain.customUnderlierPrice, DynProperty.DOUBLE, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesAggregationStyle.CustomUnderlierPrice;
        dp.minimalValue = 0;
        dp.maximalValue = 99999999;
        dp.increment = 0.01;
        dp.decimalPlaces = 2;
        properties.push(dp);
        dp = new DynProperty('property.ColoringMethod', this.getStrikesColoringMethod(), DynProperty.COMBOBOX, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(StrikesColoringMethod, getStrikesColoringMethodKey);
        dp.COMBOBOX_TYPE = DynProperty.INTEGER;
        dp.assignedProperty = [
            'property.InTheMoneyColor',
            'property.OutTheMoneyColor',
            'property.CurrentHV',
            'property.Price',
            'property.ATMStrikeColor',
            'property.StandardDeviationColor',
            'property.SecondStandardDeviationColor'
        ];
        dp.DynPropertyRelationType = DynPropertyRelationType.Visibility;
        dp.visible = this.isAllowHVColoring();
        properties.push(dp);
        dp = new DynProperty('property.InTheMoneyColor', this._optionTrader.optionChain.inTheMoneyColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.Classic;
        properties.push(dp);
        dp = new DynProperty('property.OutTheMoneyColor', this._optionTrader.optionChain.outTheMoneyColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.Classic;
        properties.push(dp);
        dp = new DynProperty('property.CurrentHV', this._optionTrader.optionChain.currentHVPercent, DynProperty.DOUBLE_WITH_REFRESH_BUTTON, DynProperty.OPTION_CHAIN_GROUP);
        dp.minimalValue = 0;
        dp.maximalValue = 1000;
        dp.increment = 0.1;
        dp.decimalPlaces = 1;
        dp.callback = () => __awaiter(this, void 0, void 0, function* () {
            return yield this._optionTrader.optionChain.CalculateHistoricalVolatilityAsync(this._optionTrader.account, this._optionTrader.underlier);
        });
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.HistoricalVolatility;
        properties.push(dp);
        dp = new DynProperty('property.Price', this._optionTrader.optionChain.historicalVolatilityPriceType, DynProperty.COMBOBOX, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.objectVariants = createContextMenuItemsFromEnum(HistoricalVolatilityPriceType, getHistoricalVolatilityPriceTypeKey);
        dp.enabilityValue = StrikesColoringMethod.HistoricalVolatility;
        properties.push(dp);
        dp = new DynProperty('property.ATMStrikeColor', this._optionTrader.optionChain.atmStrikeColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.HistoricalVolatility;
        properties.push(dp);
        dp = new DynProperty('property.StandardDeviationColor', this._optionTrader.optionChain.standardDeviationColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.HistoricalVolatility;
        properties.push(dp);
        dp = new DynProperty('property.SecondStandardDeviationColor', this._optionTrader.optionChain.secondStandardDeviationColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        dp.enabilityValue = StrikesColoringMethod.HistoricalVolatility;
        properties.push(dp);
        dp = new DynProperty('property.AskColor', this._optionTrader.optionChain.askColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        dp = new DynProperty('property.BidColor', this._optionTrader.optionChain.bidColor, DynProperty.COLOR, DynProperty.OPTION_CHAIN_GROUP);
        dp.sortIndex = index++;
        dp.separatorGroup = separatorGroup;
        properties.push(dp);
        return properties;
    }
    callBack(properties) {
        super.callBack(properties);
        const group = DynProperty.OPTION_CHAIN_GROUP;
        let dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.StrikesAggregationStyle');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.strikesAggregationStyle = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.CustomUnderlierPrice');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.customUnderlierPrice = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.ColoringMethod');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.coloringMethod = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.InTheMoneyColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.inTheMoneyColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.OutTheMoneyColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.outTheMoneyColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.CurrentHV');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.currentHVPercent = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.Price');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.historicalVolatilityPriceType = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.ATMStrikeColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.atmStrikeColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.StandardDeviationColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.standardDeviationColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.SecondStandardDeviationColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.secondStandardDeviationColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.AskColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.askColor = dp.value;
        }
        dp = DynProperty.getPropertyByGroupAndName(properties, group, 'property.BidColor');
        if (!isNullOrUndefined(dp === null || dp === void 0 ? void 0 : dp.value)) {
            this._optionTrader.optionChain.bidColor = dp.value;
        }
        this._isRecolorRows = true;
        this._isRecenterTable = true;
    }
    updateData() {
        this._callsQuickTable.updateRowsCellsValues();
        this._putsQuickTable.updateRowsCellsValues();
    }
    // #endregion
    // #region  EventHandlers
    onInstrumentChanged() {
        this.populateSeriesCombobox();
    }
    onSelectedSerieComboboxItemChanged(newSerieComboboxItem, prevSerieComboboxItem) {
        if (!this._isInitialized) {
            return;
        }
        if (+newSerieComboboxItem === +prevSerieComboboxItem) {
            return;
        }
        const date = !isNullOrUndefined(newSerieComboboxItem) ? newSerieComboboxItem.value : undefined;
        this._optionTrader.seriesDate = date;
    }
    onVisibleChanged() {
        if (!this._isInitialized) {
            return;
        }
        this.layoutTable();
    }
    onQuickTableScroll(index) {
        this._callsQuickTable.scroll.moveScrollToElement(index);
        this._putsQuickTable.scroll.moveScrollToElement(index);
        this._strikesListQuickTable.scroll.moveScrollToElement(index);
    }
    onQuickTableSelectionChanged(fromMouseClick, sender) {
        const selectedRowIndexes = sender.getSelectedRowsIndexes();
        if (this._callsQuickTable !== sender) {
            this._callsQuickTable.setSelectedRowsByIndexes(selectedRowIndexes);
        }
        if (this._putsQuickTable !== sender) {
            this._putsQuickTable.setSelectedRowsByIndexes(selectedRowIndexes);
        }
        if (this._strikesListQuickTable !== sender) {
            this._strikesListQuickTable.setSelectedRowsByIndexes(selectedRowIndexes);
        }
    }
    onQuickTableHoverChanged(sender, rowIndex) {
        if (this._callsQuickTable !== sender) {
            this._callsQuickTable.SetHoverRow(rowIndex);
        }
        if (this._putsQuickTable !== sender) {
            this._putsQuickTable.SetHoverRow(rowIndex);
        }
        if (this._strikesListQuickTable !== sender) {
            this._strikesListQuickTable.SetHoverRow(rowIndex);
        }
    }
    onQuickTableVisibleColumnChanged(indexes, sender) {
        if (this._isSkipColumnsVisibleChangedEvent) {
            return;
        }
        this._isSkipColumnsVisibleChangedEvent = true;
        if (sender === this._callsQuickTable) {
            this._putsQuickTable.setVisibleColumn(indexes);
        }
        else {
            this._callsQuickTable.setVisibleColumn(indexes);
        }
        this._isSkipColumnsVisibleChangedEvent = false;
    }
    onAfterEditItem(data) {
        const tableItem = data.row.item;
        switch (data.realColumnIndex) {
            case OptionChainTableItem.PAPER_POSITION_COL_INDEX:
                {
                    const instrument = tableItem.instrument;
                    const newAmount = data.newValue;
                    if (newAmount !== 0) {
                        let paperPosition = this._optionTrader.getPaperPosition(instrument);
                        if (isNullOrUndefined(paperPosition)) {
                            this._optionTrader.createPaperPosition(instrument);
                            paperPosition = this._optionTrader.getPaperPosition(instrument);
                        }
                        const lots = Quantity.convertAmountToLots(newAmount, instrument);
                        paperPosition.QuantityLots = Math.abs(lots);
                        paperPosition.Operation = lots > 0 ? OperationType.Buy : OperationType.Sell;
                        if (paperPosition.OrderType === OrderType.Limit) {
                            paperPosition.Price = paperPosition.Bid;
                        }
                        this._optionTrader.updatePaperPosition(instrument);
                    }
                    else {
                        this._optionTrader.removePaperPosition(instrument);
                    }
                }
                break;
        }
    }
    onColumnDisplayIndexChanged(sender, index) {
        const columnsCount = sender.columns.length;
        const mirrorTable = sender === this._callsQuickTable ? this._putsQuickTable : this._callsQuickTable;
        for (let i = 0; i < columnsCount; i++) {
            mirrorTable.columns[i].displayedIndex = columnsCount - sender.columns[i].displayedIndex - 1;
        }
        mirrorTable.UpdateSortedColumns();
        mirrorTable.needRedrawBackground = true;
        mirrorTable.needRedraw = true;
    }
    onTableMouseDoubleClick(sender, hitTestInfo) {
        var _a;
        const selectedItem = sender.getSelectedTableItems()[0];
        if (isNullOrUndefined(selectedItem) || hitTestInfo.zone !== QuickTableZone.Table) {
            return;
        }
        const columnIndex = hitTestInfo.realColumnIndex;
        if (columnIndex !== OptionChainTableItem.BID_COL_INDEX &&
            columnIndex !== OptionChainTableItem.ASK_COL_INDEX &&
            columnIndex !== OptionChainTableItem.LAST_PRICE_COL_INDEX) {
            return;
        }
        const price = selectedItem.getColumnValue(columnIndex);
        const account = this._optionTrader.account;
        const instrument = selectedItem.instrument;
        (_a = MainWindowManager.Factory) === null || _a === void 0 ? void 0 : _a.addPanel(PanelNames.AdvancedOrderEntry, null, (panel) => __awaiter(this, void 0, void 0, function* () {
            yield panel.set({ account });
            yield panel.set({ instrument });
            yield panel.set({ side: OperationType.Buy });
            panel.orderEdit.setBasePrice(price);
        }));
    }
    onQuoteChanged() {
        this._isRecolorRows = true;
    }
    // #endregion
    setOptionTrader(optionTrader) {
        this._optionTrader = optionTrader;
        this._optionTrader.subscribeOnInstrumentChanged(this.onInstrumentChanged);
        this._optionTrader.subscribeOnSeriesDateChanged(this.populateStrikes);
        this._optionTrader.subscribeOnQuoteChanged(this.onQuoteChanged);
    }
    // #region Heper methods
    initPutCallQuickTable(quickTable) {
        const isPut = quickTable === this._putsQuickTable;
        quickTable.allowGroupBy = false;
        quickTable.lockManualSorting = true;
        quickTable.InitializeDirect(new OptionChainTableItem(isPut ? OptionPutCall.OPTION_PUT_VANILLA : OptionPutCall.OPTION_CALL_VANILLA, null, null));
        quickTable.UpdateSortedColumns();
        if (!isPut) {
            quickTable.SetScrollHidden(true);
        }
        quickTable.beforeTableContextMenuShowing = this.onBeforeTableContextMenuShowing;
        quickTable.OnSelectionChanged.Subscribe(this.onQuickTableSelectionChanged, this);
        quickTable.OnHoverChanged.Subscribe(this.onQuickTableHoverChanged, this);
        quickTable.OnVisibleColumnChanged.Subscribe(this.onQuickTableVisibleColumnChanged, this);
        quickTable.scroll.OnValueChange.Subscribe(this.onQuickTableScroll, this);
        quickTable.AfterEditItem.Subscribe(this.onAfterEditItem, this);
        quickTable.ColumnDisplayIndexChanged.Subscribe(this.onColumnDisplayIndexChanged, this);
        quickTable.onTableMouseDoubleClick.Subscribe(this.onTableMouseDoubleClick, this);
        // Mirror the table
        if (isPut) {
            for (let i = 0; i < quickTable.columns.length; i++) {
                const column = quickTable.columns[i];
                column.displayedIndex = quickTable.columns.length - column.displayedIndex - 1;
            }
            quickTable.UpdateSortedColumns();
            quickTable.needRedrawBackground = true;
            quickTable.needRedraw = true;
        }
    }
    teardownPutCallQuickTable(quickTable) {
        quickTable.OnSelectionChanged.UnSubscribe(this.onQuickTableSelectionChanged, this);
        quickTable.OnHoverChanged.UnSubscribe(this.onQuickTableHoverChanged, this);
        quickTable.OnVisibleColumnChanged.UnSubscribe(this.onQuickTableVisibleColumnChanged, this);
        quickTable.scroll.OnValueChange.UnSubscribe(this.onQuickTableScroll, this);
        quickTable.AfterEditItem.UnSubscribe(this.onAfterEditItem, this);
        quickTable.ColumnDisplayIndexChanged.UnSubscribe(this.onColumnDisplayIndexChanged, this);
        quickTable.onTableMouseDoubleClick.UnSubscribe(this.onTableMouseDoubleClick, this);
    }
    initializeVariables() {
        const tables = super.findAllComponents('quickTableRactive');
        for (let i = 0; i < tables.length; i++) {
            const quickTableRactive = tables[i];
            switch (quickTableRactive.get('name')) {
                case 'callsTable':
                    this._callsQuickTable = quickTableRactive.quickTable;
                    break;
                case 'putsTable':
                    this._putsQuickTable = quickTableRactive.quickTable;
                    break;
                case 'strikesListTable':
                    this._strikesListQuickTable = quickTableRactive.quickTable;
                    break;
            }
        }
        this._isInitialized = true;
    }
    populateSeriesCombobox() {
        const seriesDates = this._optionTrader.seriesDates;
        const newSeriesComboboxItems = seriesDates.map((dateString) => {
            const seriesDate = new Date(dateString);
            const daysToExpiration = OptionTraderUtils.getDaysToExpirationForDate(seriesDate);
            return {
                type: NewContextMenuItemType.Default,
                text: OptionTraderUtils.formatExpirationDateWithDays(seriesDate, daysToExpiration),
                value: seriesDate
            };
        });
        void super.set('seriesComboboxItems', newSeriesComboboxItems);
        void super.set('selectedSerieComboboxItem', newSeriesComboboxItems[OptionTraderUtils.getNearestExpirationDateIndex(seriesDates)]);
    }
    populateStrikes() {
        if (!this._isInitialized) {
            return;
        }
        this._callsQuickTable.ClearAll();
        this._putsQuickTable.ClearAll();
        this._strikesListQuickTable.ClearAll();
        const options = this._optionTrader.options;
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            const putTableItem = new OptionChainTableItem(OptionPutCall.OPTION_PUT_VANILLA, option.put, this._optionTrader);
            const callTableItem = new OptionChainTableItem(OptionPutCall.OPTION_CALL_VANILLA, option.call, this._optionTrader);
            const strikeListTableItem = new OptionChainStrikesListTableItem(option);
            this._putsQuickTable.AddItem(putTableItem);
            this._callsQuickTable.AddItem(callTableItem);
            this._strikesListQuickTable.AddItem(strikeListTableItem);
        }
        this._isRecolorRows = true;
        this._isRecenterTable = true;
    }
    updateTableEditingInfo(table) {
        const rows = table.rowsArray;
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].isGroupRow) {
                continue;
            }
            const item = rows[i].item;
            item.updateEditingInfo();
        }
    }
    isAllowHVColoring() {
        return this._optionTrader.optionChain.isAllowHVColoring(this._optionTrader.fakeOption);
    }
    getStrikesColoringMethod() {
        return this.isAllowHVColoring() ? this._optionTrader.optionChain.coloringMethod : StrikesColoringMethod.Classic;
    }
    recolorRows(quickTable) {
        const coloringMode = this.getStrikesColoringMethod();
        switch (coloringMode) {
            case StrikesColoringMethod.Classic:
                this.recolorClassic(quickTable);
                break;
            case StrikesColoringMethod.HistoricalVolatility:
                this.recolorHistoricalVolatility(quickTable);
                break;
        }
    }
    recolorClassic(quickTable) {
        const optionChain = this._optionTrader.optionChain;
        const account = this._optionTrader.account;
        const underlier = this._optionTrader.underlier;
        const rows = quickTable.rowsArray;
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].isGroupRow) {
                continue;
            }
            const row = rows[i];
            const item = row.item;
            if (isNullOrUndefined(item.instrument)) {
                continue;
            }
            row.BackColor = optionChain.getColorInOutMoney(account, underlier, item.instrument);
            row.cells[OptionChainTableItem.ASK_COL_INDEX].BackColor = optionChain.askColor;
            row.cells[OptionChainTableItem.BID_COL_INDEX].BackColor = optionChain.bidColor;
        }
    }
    recolorHistoricalVolatility(quickTable) {
        const optionChain = this._optionTrader.optionChain;
        const rows = quickTable.rowsArray;
        const historicalVolatilityData = optionChain.getHistoricalVolatilityData(this._optionTrader.account, this._optionTrader.underlier);
        let bottomNearest;
        let topNearest;
        let equal;
        // Level1/Level2
        for (let i = 0; i < rows.length; i++) {
            if (rows[i].isGroupRow) {
                continue;
            }
            const row = rows[i];
            const item = row.item;
            if (isNullOrUndefined(item.instrument)) {
                continue;
            }
            row.BackColor = null;
            row.cells[OptionChainTableItem.ASK_COL_INDEX].BackColor = optionChain.askColor;
            row.cells[OptionChainTableItem.BID_COL_INDEX].BackColor = optionChain.bidColor;
            const strike = item.instrument.StrikePrice;
            if (strike >= historicalVolatilityData.lvl2Left && strike <= historicalVolatilityData.lvl2Right) {
                row.BackColor = optionChain.secondStandardDeviationColor;
            }
            if (strike >= historicalVolatilityData.lvl1Left && strike <= historicalVolatilityData.lvl1Right) {
                row.BackColor = optionChain.standardDeviationColor;
            }
            if (!isNullOrUndefined(equal) && strike === historicalVolatilityData.lastPrice) {
                equal = row;
                bottomNearest = undefined;
                topNearest = undefined;
            }
            if (isNullOrUndefined(equal) && isNullOrUndefined(topNearest)) {
                if (historicalVolatilityData.lastPrice - strike > 0) {
                    bottomNearest = row;
                }
                else {
                    topNearest = row;
                }
            }
        }
        if (!isNullOrUndefined(equal)) {
            equal.BackColor = optionChain.atmStrikeColor;
        }
        else if (!isNullOrUndefined(topNearest) && !isNullOrUndefined(bottomNearest)) {
            topNearest.BackColor = optionChain.atmStrikeColor;
            bottomNearest.BackColor = optionChain.atmStrikeColor;
        }
    }
    recenterTable(centerPrice) {
        let centerIndex = 0;
        const rowsCountByHeight = this._strikesListQuickTable.getRowsCountByHeight();
        const rowsArray = this._strikesListQuickTable.rowsArray;
        for (let i = 0; i < rowsArray.length; i++) {
            if (rowsArray[i].isGroupRow) {
                continue;
            }
            const item = rowsArray[i].item;
            if (item.strike <= centerPrice) {
                centerIndex = i;
            }
            else {
                break;
            }
        }
        centerIndex = Math.max(0, Math.floor(centerIndex - rowsCountByHeight / 2));
        this.onQuickTableScroll(centerIndex);
    }
    getCenterPrice() {
        if (!this._isInitialized) {
            return NaN;
        }
        const optionChain = this._optionTrader.optionChain;
        return optionChain.strikesAggregationStyle === StrikesAggregationStyle.CustomUnderlierPrice
            ? optionChain.customUnderlierPrice
            : this._optionTrader.getUnderlierPrice();
    }
}
ApplicationPanelWithTable.extendWith(OptionChainPanel, {
    template: OptionChainPanelTemplate,
    data: function () {
        return {
            callsLabel: '',
            putsLabel: '',
            seriesComboboxItems: [],
            selectedSerieComboboxItem: null
        };
    }
});
